export default function TermsOfService() {
  return (
    <div className="bg-white px-6 py-32 lg:px-8">
      <div className="mx-auto max-w-4xl text-base/7 text-gray-700">
        <p className="text-base/7 font-semibold text-indigo-600">Last Updated: October 21, 2025</p>
        <h1 className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
          Terms of Service
        </h1>

        <div className="mt-10 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="mb-4">
              Welcome to Wonderstood ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and
              use of the Wonderstood platform, website, and services (collectively, the "Platform").
            </p>
            <p className="mb-4">
              <strong>By accessing or using the Platform, you agree to be bound by these Terms.</strong> If you do not
              agree to these Terms, you may not access or use the Platform.
            </p>
            <p className="mb-4">
              These Terms constitute a legally binding agreement between you and Wonderstood, Inc., a corporation organized
              under the laws of [State], United States.
            </p>
            <p className="mb-4 font-semibold">
              PLEASE READ THESE TERMS CAREFULLY. THEY CONTAIN IMPORTANT INFORMATION ABOUT YOUR LEGAL RIGHTS, INCLUDING
              MANDATORY ARBITRATION AND CLASS ACTION WAIVER PROVISIONS IN SECTION 16.
            </p>
          </section>

          {/* Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Eligibility</h2>
            <p className="mb-4">
              You must be at least 18 years old to use the Platform. By creating an account, you represent and warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>You are at least 18 years of age</li>
              <li>You have the legal capacity to enter into these Terms</li>
              <li>You are not prohibited from using the Platform under applicable law</li>
              <li>All information you provide is accurate and complete</li>
              <li>You will maintain the accuracy of your information</li>
            </ul>
            <p className="mb-4">
              We reserve the right to refuse service, terminate accounts, or cancel bookings at our sole discretion if
              we suspect you do not meet these eligibility requirements.
            </p>
          </section>

          {/* Platform Description */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Platform Description and Role</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Wonderstood's Role as Third-Party Marketplace</h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="font-semibold text-yellow-900 mb-2">IMPORTANT DISCLAIMER:</p>
              <p className="text-yellow-900">
                <strong>Wonderstood operates as a third-party marketplace platform connecting independent professionals
                with clients seeking coaching, mentoring, and consulting services. Wonderstood is NOT a party to the
                service relationship between professionals and clients.</strong>
              </p>
            </div>

            <p className="mb-4">
              The Platform provides:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>A marketplace for professionals to offer their services</li>
              <li>Tools for clients to search, discover, and book professionals</li>
              <li>Payment processing facilitation through Stripe</li>
              <li>Video calling infrastructure through Daily.co</li>
              <li>Scheduling and booking management</li>
              <li>Communication tools for users</li>
              <li>Review and rating systems</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Independent Contractor Relationship</h3>
            <p className="mb-4">
              <strong>All professionals on the Platform are independent contractors engaged in their own businesses.</strong>
              Professionals are NOT employees, agents, or representatives of Wonderstood. Each professional:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Sets their own rates and availability</li>
              <li>Controls the manner and means of providing services</li>
              <li>Is responsible for their own taxes, insurance, and licensing</li>
              <li>Maintains their own professional credentials and qualifications</li>
              <li>Operates independently from Wonderstood's direction or control</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 No Professional Services from Wonderstood</h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="font-semibold text-yellow-900 mb-2">DISCLAIMER OF PROFESSIONAL ADVICE:</p>
              <p className="text-yellow-900">
                <strong>WONDERSTOOD DOES NOT PROVIDE COACHING, CONSULTING, OR PROFESSIONAL ADVICE OF ANY KIND. All
                services are provided solely by independent third-party professionals. Wonderstood makes no representations
                or warranties about the qualifications, competence, accuracy, reliability, or quality of services provided
                by professionals on the Platform.</strong>
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 No Guaranteed Results</h3>
            <p className="mb-4">
              <strong>Results from coaching, mentoring, and consulting services are not guaranteed.</strong> Client
              outcomes depend on individual circumstances, effort, commitment, and many factors outside of anyone's control.
              Wonderstood is not responsible for any results, outcomes, or lack thereof from services obtained through
              the Platform.
            </p>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Account Registration and Security</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Account Creation</h3>
            <p className="mb-4">
              To use the Platform, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Not share your account credentials with others</li>
              <li>Not create multiple accounts or impersonate others</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Account Types</h3>
            <p className="mb-4">You may register as either:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Client:</strong> Seeking to book and receive professional services</li>
              <li><strong>Professional:</strong> Offering coaching, mentoring, or consulting services</li>
            </ul>
            <p className="mb-4">
              You may have only one account type at a time. Switching account types requires contacting support.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Account Security</h3>
            <p className="mb-4">
              You are responsible for all activity that occurs under your account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Choose a strong, unique password</li>
              <li>Enable two-factor authentication (highly recommended)</li>
              <li>Log out from shared or public devices</li>
              <li>Report suspected security breaches immediately to support@wonderstood.com</li>
            </ul>
            <p className="mb-4 font-semibold">
              We are not liable for any loss or damage arising from your failure to maintain account security.
            </p>
          </section>

          {/* Professional Obligations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Professional User Obligations</h2>
            <p className="mb-4">
              If you register as a professional, you agree to comply with the following additional obligations:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Credentials and Licensing</h3>
            <p className="mb-4">You represent and warrant that you:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Hold all necessary licenses, certifications, and credentials required for the services you offer</li>
              <li>Comply with all applicable professional standards and regulations in your jurisdiction</li>
              <li>Maintain current professional liability insurance (strongly recommended, may be required for certain services)</li>
              <li>Will provide accurate and truthful information about your qualifications and experience</li>
              <li>Will not misrepresent your credentials or experience</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Scope of Services</h3>
            <p className="mb-4">You agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Practice only within your area of competence and credentials</li>
              <li>Decline services that fall outside your scope of practice</li>
              <li>Refer clients to appropriate licensed professionals when necessary (e.g., mental health, medical, legal issues)</li>
              <li>Not provide medical, legal, accounting, or other licensed professional advice unless properly licensed</li>
              <li>Clearly communicate limitations and scope of your services to clients</li>
            </ul>

            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <p className="font-semibold text-red-900 mb-2">PROHIBITED SERVICES:</p>
              <p className="text-red-900">
                Unless properly licensed, you may NOT provide:
              </p>
              <ul className="list-disc pl-6 mt-2 text-red-900">
                <li>Mental health therapy or psychological treatment</li>
                <li>Medical diagnosis or treatment</li>
                <li>Legal advice or representation</li>
                <li>Financial/investment advice (unless licensed financial advisor)</li>
                <li>Tax preparation or accounting services (unless licensed CPA/accountant)</li>
              </ul>
              <p className="mt-2 text-red-900">
                <strong>Violations will result in immediate account termination.</strong>
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Professional Standards</h3>
            <p className="mb-4">You agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Provide services professionally and ethically</li>
              <li>Honor scheduled appointments or cancel according to the Cancellation Policy</li>
              <li>Respond to booking requests within 48 hours</li>
              <li>Maintain professional boundaries with clients</li>
              <li>Protect client confidentiality (subject to legal exceptions)</li>
              <li>Comply with applicable professional codes of ethics</li>
              <li>Not engage in dual relationships that could impair professional judgment</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.4 Taxes and Financial Responsibilities</h3>
            <p className="mb-4">As an independent contractor, you are responsible for:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Reporting all income earned through the Platform</li>
              <li>Paying all applicable federal, state, and local taxes</li>
              <li>Paying self-employment taxes</li>
              <li>Maintaining accurate financial records</li>
              <li>Providing tax identification information to Stripe for 1099 reporting</li>
            </ul>
            <p className="mb-4 italic">
              Note: Wonderstood will report payments to professionals to the IRS via Form 1099-K (processed by Stripe)
              if you meet the reporting thresholds.
            </p>
          </section>

          {/* Client Obligations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Client User Obligations</h2>
            <p className="mb-4">
              If you register as a client, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Provide accurate information when booking services</li>
              <li>Attend scheduled sessions or cancel according to the Cancellation Policy</li>
              <li>Treat professionals with respect and professionalism</li>
              <li>Pay for services as agreed</li>
              <li>Not record sessions without explicit consent from all participants</li>
              <li>Not share, reproduce, or commercialize professionals' proprietary materials without permission</li>
              <li>Take personal responsibility for decisions and actions based on advice or guidance received</li>
              <li>Understand that professional services are not a substitute for medical, legal, or other licensed professional advice</li>
            </ul>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="font-semibold text-yellow-900 mb-2">YOUR RESPONSIBILITY:</p>
              <p className="text-yellow-900">
                You are solely responsible for evaluating whether a professional's services are appropriate for your
                needs. We recommend:
              </p>
              <ul className="list-disc pl-6 mt-2 text-yellow-900">
                <li>Independently verifying professional credentials</li>
                <li>Reading reviews and ratings carefully</li>
                <li>Asking questions before booking</li>
                <li>Seeking licensed professional help for medical, legal, or mental health issues</li>
              </ul>
            </div>
          </section>

          {/* Booking and Cancellation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Booking, Scheduling, and Cancellation</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.1 Booking Process</h3>
            <p className="mb-4">When you book a session:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>You agree to the professional's stated hourly rate</li>
              <li>Payment is charged immediately via Stripe</li>
              <li>Payment includes the professional's fee plus the platform fee (15%)</li>
              <li>You will receive a booking confirmation via email</li>
              <li>Session details (date, time, video call link) will be provided in your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.2 Cancellation Policy</h3>
            <p className="mb-4">
              Our Cancellation Policy governs refunds and cancellations. Key provisions:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>More than 48 hours before:</strong> Full refund (minus payment processing fees)</li>
              <li><strong>24-48 hours before:</strong> 50% refund</li>
              <li><strong>Less than 24 hours before:</strong> No refund</li>
              <li><strong>No-shows:</strong> Refund upon manual review</li>
              <li><strong>Professional cancellations:</strong> Full refund</li>
            </ul>
            <p className="mb-4">
              See our complete <a href="/cancellation-policy" className="text-indigo-600 hover:text-indigo-800 underline">Cancellation Policy</a> for full details.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.3 Session Modifications</h3>
            <p className="mb-4">
              To reschedule a session, you must cancel the existing booking (subject to cancellation terms) and create
              a new booking. Direct rescheduling may be available in the future.
            </p>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Payment Terms</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 Payment Processing</h3>
            <p className="mb-4">
              All payments are processed through Stripe, Inc., a third-party payment processor. By using the Platform, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Provide valid payment information</li>
              <li>Authorize Stripe to charge your payment method</li>
              <li>Comply with Stripe's Terms of Service</li>
              <li>Accept that Wonderstood does not store complete payment card information</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 Platform Fee</h3>
            <p className="mb-4">
              Wonderstood charges a <strong>15% platform fee</strong> on all transactions. Fee breakdown:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Deducted from the gross transaction before professional payout</li>
              <li>Covers platform maintenance, payment processing, customer support, dispute resolution</li>
              <li>Non-refundable, even if the service is refunded</li>
              <li>Displayed at booking time in the total cost</li>
            </ul>
            <p className="mb-4">
              <strong>Example:</strong> $100 session = $15 platform fee + $85 to professional (before Stripe processing fees)
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.3 Payment Escrow</h3>
            <p className="mb-4">
              Client payments are held in escrow to protect both parties:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Escrow period:</strong> 7 days from the scheduled meeting date</li>
              <li><strong>Protection against:</strong> Chargebacks, fraud, service quality disputes, no-shows</li>
              <li><strong>Professional payout:</strong> Automatically released to professional's Stripe account after escrow period</li>
              <li><strong>Disputes:</strong> Must be filed within the escrow period (see Section 9)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.4 Refunds</h3>
            <p className="mb-4">
              Refunds are governed by our Cancellation Policy. Generally:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Refunds processed to original payment method</li>
              <li>Stripe payment processing fees are non-refundable</li>
              <li>Refunds may take 5-10 business days to appear in your account</li>
              <li>Platform fees are non-refundable</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.5 Chargebacks</h3>
            <p className="mb-4 font-semibold">
              IMPORTANT: Please contact support before initiating a chargeback with your bank.
            </p>
            <p className="mb-4">
              Clients agree to contact Wonderstood support at support@wonderstood.com before disputing charges with their
              bank or card issuer. Initiating a chargeback without attempting platform resolution may result in:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Immediate account suspension pending investigation</li>
              <li>Permanent account termination if chargeback is deemed fraudulent</li>
              <li>Collection efforts for amounts owed</li>
              <li>Liability for chargeback fees and legal costs</li>
            </ul>
            <p className="mb-4">
              Professionals agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Provide evidence to defend against chargebacks</li>
              <li>Respond to chargeback inquiries within 48 hours</li>
              <li>Maintain session records for 180 days</li>
            </ul>
            <p className="mb-4">
              Chargeback fees ($15-25) may be deducted from professional payouts for chargebacks resulting from
              professional conduct (fraud, non-delivery, misrepresentation).
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.6 Taxes</h3>
            <p className="mb-4">
              All fees and charges are exclusive of applicable taxes. You are responsible for paying all taxes associated
              with your use of the Platform.
            </p>
          </section>

          {/* Dispute Resolution Between Users */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disputes Between Users</h2>
            <p className="mb-4">
              Disputes may arise between clients and professionals regarding service quality, payment, conduct, or other issues.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">9.1 Platform's Limited Role</h3>
            <p className="mb-4">
              Wonderstood is not a party to disputes between users. However, we may, at our sole discretion, assist
              with dispute resolution by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Reviewing evidence from both parties</li>
              <li>Facilitating communication</li>
              <li>Offering mediation</li>
              <li>Providing refunds or credits in appropriate circumstances</li>
              <li>Suspending or terminating accounts for policy violations</li>
            </ul>
            <p className="mb-4 font-semibold">
              We are not obligated to resolve disputes and reserve the right to decline involvement at our discretion.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">9.2 Dispute Resolution Process</h3>
            <p className="mb-4"><strong>Step 1: Attempt Direct Resolution</strong></p>
            <p className="mb-4">
              First, attempt to resolve the issue directly with the other party through platform messaging.
            </p>

            <p className="mb-4"><strong>Step 2: Contact Support</strong></p>
            <p className="mb-4">
              If direct resolution fails, contact support@wonderstood.com within the escrow period (7 days) with:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Detailed description of the issue</li>
              <li>Supporting evidence (screenshots, messages, session notes)</li>
              <li>Desired resolution</li>
            </ul>

            <p className="mb-4"><strong>Step 3: Platform Review</strong></p>
            <p className="mb-4">
              We will:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Review evidence from both parties</li>
              <li>Request additional information if needed</li>
              <li>Make a determination within 5-7 business days</li>
              <li>Communicate the outcome to both parties</li>
            </ul>

            <p className="mb-4"><strong>Step 4: Possible Outcomes</strong></p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Full or partial refund to client</li>
              <li>Platform credit</li>
              <li>No refund (payment released to professional)</li>
              <li>Account warning, suspension, or termination for policy violations</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">9.3 Release of Liability</h3>
            <p className="mb-4">
              <strong>By using the Platform, you release Wonderstood from all claims, demands, and damages arising out
              of disputes with other users.</strong> This includes, but is not limited to, claims for negligence, fraud,
              misrepresentation, or breach of contract by other users.
            </p>
          </section>

          {/* Prohibited Uses */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Prohibited Uses and Conduct</h2>
            <p className="mb-4">
              You may not use the Platform for any unlawful purpose or in any way that violates these Terms. Specifically, you may NOT:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Professional Misconduct</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Misrepresent your qualifications, credentials, or experience</li>
              <li>Provide services outside your scope of license or certification</li>
              <li>Engage in unethical professional conduct</li>
              <li>Share client confidential information (except as legally required)</li>
              <li>Solicit clients to transact outside the Platform to avoid fees</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Client Misconduct</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Book sessions without intent to attend</li>
              <li>Make false claims or fraudulent chargebacks</li>
              <li>Harass, threaten, or abuse professionals</li>
              <li>Request illegal or unethical services</li>
              <li>Record sessions without all-party consent</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">General Prohibited Conduct</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Violate any local, state, federal, or international laws or regulations</li>
              <li>Infringe any intellectual property rights or other proprietary rights</li>
              <li>Transmit viruses, malware, or other harmful code</li>
              <li>Engage in fraudulent activity or identity theft</li>
              <li>Create multiple accounts to manipulate the system</li>
              <li>Use automated bots, scrapers, or data mining tools</li>
              <li>Impersonate others or provide false information</li>
              <li>Send spam or unsolicited communications</li>
              <li>Discriminate based on race, ethnicity, national origin, religion, gender, sexual orientation, disability, age, or other protected characteristics</li>
              <li>Harass, bully, threaten, or intimidate other users</li>
              <li>Make unwanted sexual advances or inappropriate comments</li>
              <li>Violate any third-party rights</li>
              <li>Attempt to reverse engineer, decompile, or disassemble the Platform</li>
              <li>Interfere with or disrupt the Platform or servers</li>
              <li>Share account credentials or access to your account</li>
              <li>Circumvent any security or authentication measures</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Platform Circumvention</h3>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <p className="font-semibold text-red-900 mb-2">PROHIBITION ON CIRCUMVENTING PLATFORM:</p>
              <p className="text-red-900 mb-2">
                Users may NOT circumvent the Platform by:
              </p>
              <ul className="list-disc pl-6 text-red-900 space-y-1">
                <li>Accepting or requesting direct payment outside the Platform</li>
                <li>Sharing contact information to arrange off-platform transactions</li>
                <li>Advertising other booking or payment methods</li>
                <li>Encouraging other users to transact directly</li>
              </ul>
              <p className="mt-2 text-red-900">
                <strong>Violations may result in immediate account termination, forfeiture of payments, and legal action
                for breach of contract.</strong>
              </p>
            </div>
            <p className="mb-4">
              This prohibition applies during your active use of the Platform and for 12 months following your last
              platform transaction with a particular user met through Wonderstood.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Intellectual Property Rights</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">11.1 Platform Content and Trademarks</h3>
            <p className="mb-4">
              Wonderstood and its licensors own all intellectual property rights in the Platform, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Website design, code, software, and architecture</li>
              <li>Wonderstood trademarks, logos, and branding</li>
              <li>Platform features, functionality, and user interface</li>
              <li>Documentation, help materials, and content we create</li>
            </ul>
            <p className="mb-4">
              You receive a limited, non-exclusive, non-transferable, revocable license to access and use the Platform
              for its intended purpose. You may not:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Copy, modify, distribute, or create derivative works of the Platform</li>
              <li>Reverse engineer, decompile, or disassemble the Platform</li>
              <li>Use Wonderstood trademarks without written permission</li>
              <li>Frame or mirror any part of the Platform</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">11.2 User-Generated Content - Professional Profiles</h3>
            <p className="mb-4">
              Professionals retain ownership of their profile content, including profiles, bios, credentials, and photos.
              By posting content, you grant Wonderstood a worldwide, non-exclusive, royalty-free, transferable license to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Display content on the Platform</li>
              <li>Reproduce content for platform operations and backups</li>
              <li>Create derivative works for marketing (e.g., testimonials, case studies) with your permission</li>
              <li>Distribute content in connection with Platform services</li>
            </ul>
            <p className="mb-4">
              You represent and warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>You own or have rights to all content you post</li>
              <li>Content does not infringe any third-party intellectual property rights</li>
              <li>Content complies with applicable laws and professional standards</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">11.3 Reviews, Ratings, and Feedback</h3>
            <p className="mb-4">
              By submitting reviews, ratings, or feedback, you grant Wonderstood a perpetual, irrevocable, worldwide,
              royalty-free, transferable license to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Display reviews on professional profiles</li>
              <li>Use reviews for platform improvement and marketing</li>
              <li>Moderate, edit (for formatting/profanity only), or remove reviews per our Content Policy</li>
            </ul>
            <p className="mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Submit false or misleading reviews</li>
              <li>Post reviews in exchange for compensation (except legitimate service receipt)</li>
              <li>Include personal information of others in reviews</li>
              <li>Violate confidentiality in reviews</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">11.4 Session Materials and Recordings</h3>
            <p className="mb-4">
              <strong>Professional Materials:</strong> Professionals retain rights to their proprietary materials, templates,
              frameworks, and methodologies. Clients may not reproduce, distribute, or commercialize professional materials
              without explicit written permission.
            </p>
            <p className="mb-4">
              <strong>Session Recordings:</strong> Recording video sessions requires all-party consent (see Section 12.3).
              Ownership of recordings defaults to joint ownership, with mutual consent required for any use beyond personal reference.
            </p>
            <p className="mb-4">
              <strong>Platform Data:</strong> Wonderstood claims no ownership of session content but retains the right
              to collect and use anonymized, aggregated data for analytics and platform improvement.
            </p>
          </section>

          {/* Video Call Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Video Call Services</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">12.1 Video Infrastructure</h3>
            <p className="mb-4">
              Video sessions are provided through Daily.co, a third-party video infrastructure provider. By participating
              in video calls, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Daily.co's Terms of Service and Privacy Policy</li>
              <li>Accept that video quality depends on your internet connection and device</li>
              <li>Understand that Wonderstood is not responsible for Daily.co service issues</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">12.2 Technical Requirements and Support</h3>
            <p className="mb-4">
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Maintaining adequate internet connection</li>
              <li>Using compatible devices and browsers</li>
              <li>Ensuring proper audio/video hardware</li>
              <li>Troubleshooting your own technical issues</li>
            </ul>
            <p className="mb-4">
              Technical support is limited to Platform features. We cannot troubleshoot internet connectivity or device issues.
            </p>
            <p className="mb-4 font-semibold">
              Wonderstood is not liable for technical difficulties, connection failures, or video quality issues during sessions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">12.3 Recording Consent Requirements</h3>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <p className="font-semibold text-red-900 mb-2">RECORDING PROHIBITED WITHOUT CONSENT:</p>
              <p className="text-red-900 mb-2">
                <strong>Recording video or audio sessions is STRICTLY PROHIBITED unless you obtain explicit written
                consent from ALL participants before the session begins.</strong>
              </p>
              <p className="text-red-900">
                In certain U.S. states (California, Connecticut, Delaware, Florida, Illinois, Maryland, Massachusetts,
                Michigan, Montana, Nevada, New Hampshire, Pennsylvania, Washington), recording without all-party consent
                is a CRIMINAL OFFENSE.
              </p>
            </div>
            <p className="mb-4">
              If you wish to record a session, you must:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Obtain written consent through platform messaging before the session</li>
              <li>Provide clear notice when recording starts</li>
              <li>Stop recording immediately if any participant withdraws consent</li>
              <li>Maintain records of consent</li>
            </ul>
            <p className="mb-4 font-semibold">
              Violations of recording consent requirements may result in immediate account termination, legal liability,
              and criminal prosecution.
            </p>
          </section>

          {/* Reviews and Ratings */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Reviews and Ratings</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">13.1 Leaving Reviews</h3>
            <p className="mb-4">
              Only clients who have completed sessions may leave reviews for professionals. Reviews must be:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Honest, accurate, and based on your personal experience</li>
              <li>Free from profanity, hate speech, or abusive language</li>
              <li>Not include personal information or confidential details about the professional</li>
              <li>Not false, misleading, or defamatory</li>
              <li>Not submitted in exchange for compensation (unless you received the service legitimately)</li>
              <li>Not retaliation for legitimate complaints or exercise of rights</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">13.2 Content Moderation</h3>
            <p className="mb-4">
              Wonderstood reserves the right to moderate reviews for:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Profanity or abusive language</li>
              <li>Personal information or confidential details</li>
              <li>False or misleading statements</li>
              <li>Conflicts of interest</li>
              <li>Legal violations or defamation</li>
              <li>Spam or promotional content</li>
            </ul>
            <p className="mb-4">
              We may edit reviews for formatting or remove reviews that violate our Content Policy. We will not remove
              reviews simply because a user disagrees with them or is unhappy with negative feedback.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">13.3 Professional Responses</h3>
            <p className="mb-4">
              Professionals may respond publicly to reviews. Responses must be professional and not:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Reveal client confidential information</li>
              <li>Harass or threaten the reviewer</li>
              <li>Contain false or defamatory statements</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">13.4 Review Disputes</h3>
            <p className="mb-4">
              If you believe a review violates our policies, you may report it to support@wonderstood.com. We will
              investigate and take appropriate action. Disputes about the accuracy or fairness of reviews are resolved
              through our Dispute Resolution process (Section 16).
            </p>
          </section>

          {/* Account Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Account Suspension and Termination</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">14.1 Termination by Wonderstood</h3>
            <p className="mb-4">
              We reserve the right to suspend or terminate user accounts at our sole discretion, with or without notice,
              for any reason, including but not limited to:
            </p>

            <p className="mb-4"><strong>Immediate Termination (No Notice):</strong></p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Illegal activity</li>
              <li>Fraud, payment abuse, or chargebacks deemed fraudulent</li>
              <li>Safety threats to other users</li>
              <li>Violation of professional licensing requirements</li>
              <li>Multiple no-shows or cancellations indicating bad faith</li>
              <li>Harassment, discrimination, or abusive conduct</li>
              <li>Circumventing the Platform to avoid fees</li>
            </ul>

            <p className="mb-4"><strong>Termination with Notice:</strong></p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Material breach of these Terms</li>
              <li>Repeated policy violations after warnings</li>
              <li>Inactivity for 180+ consecutive days</li>
              <li>Multiple professional conduct complaints (after investigation)</li>
              <li>Failure to maintain required credentials or insurance</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">14.2 Effect of Termination</h3>
            <p className="mb-4">
              Upon account termination:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>You immediately lose access to your account and all Platform features</li>
              <li>Scheduled sessions may be cancelled (refunds subject to reason for termination)</li>
              <li>Professionals forfeit pending payouts if terminated for fraud or serious violations</li>
              <li>Escrow funds are processed per existing obligations for non-fraud terminations</li>
              <li>You may not create a new account without our written permission</li>
              <li>Platform fees paid are not refundable</li>
              <li>You remain liable for any outstanding payments or obligations</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">14.3 Survival</h3>
            <p className="mb-4">
              The following provisions survive termination:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Payment obligations</li>
              <li>Intellectual property licenses you've granted</li>
              <li>Disclaimers of warranties</li>
              <li>Limitations of liability</li>
              <li>Indemnification obligations</li>
              <li>Dispute resolution and arbitration provisions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">14.4 Voluntary Account Deletion</h3>
            <p className="mb-4">
              You may delete your account at any time from account settings. Upon deletion:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Your account is immediately deactivated</li>
              <li>Most personal information is deleted within 90 days</li>
              <li>Some data retained for legal compliance (see Privacy Policy)</li>
              <li>Reviews you've written remain visible but anonymized</li>
              <li>Pending professional payouts will be processed</li>
              <li>Account deletion is permanent and cannot be undone</li>
            </ul>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Disclaimers of Warranties</h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="font-semibold text-yellow-900 mb-2">PLEASE READ CAREFULLY:</p>
              <p className="text-yellow-900">
                THE FOLLOWING DISCLAIMERS LIMIT OUR LIABILITY TO YOU. BY USING THE PLATFORM, YOU AGREE TO THESE DISCLAIMERS.
              </p>
            </div>

            <p className="mb-4 font-semibold uppercase">
              THE PLATFORM AND ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>

            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>IMPLIED WARRANTIES OF MERCHANTABILITY</li>
              <li>IMPLIED WARRANTIES OF FITNESS FOR A PARTICULAR PURPOSE</li>
              <li>IMPLIED WARRANTIES OF NON-INFRINGEMENT</li>
              <li>WARRANTIES OF UNINTERRUPTED OR ERROR-FREE OPERATION</li>
              <li>WARRANTIES REGARDING ACCURACY, RELIABILITY, OR QUALITY OF SERVICES</li>
              <li>WARRANTIES REGARDING PROFESSIONAL QUALIFICATIONS, COMPETENCE, OR PERFORMANCE</li>
              <li>WARRANTIES THAT THE PLATFORM WILL MEET YOUR REQUIREMENTS</li>
              <li>WARRANTIES THAT DEFECTS WILL BE CORRECTED</li>
            </ul>

            <p className="mb-4 font-semibold uppercase">
              WONDERSTOOD DOES NOT WARRANT THAT:
            </p>

            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>The Platform will be available at all times or without interruption</li>
              <li>The Platform is free from viruses, malware, or other harmful components</li>
              <li>Results from professional services will meet your expectations</li>
              <li>Professionals are properly licensed, qualified, or competent (despite our verification efforts)</li>
              <li>Video calls will be uninterrupted or of a certain quality</li>
              <li>Payment processing will be error-free</li>
              <li>User-generated content (reviews, profiles) is accurate or reliable</li>
              <li>The Platform will achieve any specific outcomes or results</li>
            </ul>

            <p className="mb-4 font-semibold">
              USE OF THE PLATFORM IS AT YOUR SOLE RISK. YOU ARE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR DEVICE OR
              LOSS OF DATA RESULTING FROM USE OF THE PLATFORM.
            </p>

            <p className="mb-4">
              Some jurisdictions do not allow the exclusion of implied warranties, so some of the above exclusions may
              not apply to you. You may have other rights that vary by jurisdiction.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Limitation of Liability and Dispute Resolution</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">16.1 Limitation of Liability</h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="font-semibold text-yellow-900 mb-2">IMPORTANT LIMITATION:</p>
              <p className="text-yellow-900 uppercase">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WONDERSTOOD'S TOTAL LIABILITY FOR ANY AND ALL CLAIMS ARISING
                FROM OR RELATED TO YOUR USE OF THE PLATFORM OR THESE TERMS SHALL NOT EXCEED THE GREATER OF:
              </p>
              <ul className="list-disc pl-6 mt-2 text-yellow-900">
                <li>THE AMOUNT YOU PAID TO WONDERSTOOD IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR</li>
                <li>$100 (ONE HUNDRED U.S. DOLLARS)</li>
              </ul>
            </div>

            <p className="mb-4 font-semibold uppercase">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WONDERSTOOD SHALL NOT BE LIABLE FOR:
            </p>

            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
              <li>LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES</li>
              <li>PERSONAL INJURY OR PROPERTY DAMAGE ARISING FROM YOUR USE OF THE PLATFORM</li>
              <li>ACTS OR OMISSIONS OF PROFESSIONALS OR OTHER USERS</li>
              <li>QUALITY, ACCURACY, OR RELIABILITY OF PROFESSIONAL SERVICES</li>
              <li>HARM CAUSED BY OTHER USERS</li>
              <li>ERRORS, MISTAKES, OR INACCURACIES OF CONTENT</li>
              <li>UNAUTHORIZED ACCESS TO YOUR ACCOUNT OR DATA</li>
              <li>INTERRUPTION OR CESSATION OF PLATFORM SERVICES</li>
              <li>BUGS, VIRUSES, OR HARMFUL CODE TRANSMITTED THROUGH THE PLATFORM</li>
              <li>THIRD-PARTY SERVICE FAILURES (STRIPE, DAILY.CO, FIREBASE)</li>
              <li>LOSS OR DAMAGE TO USER-GENERATED CONTENT</li>
            </ul>

            <p className="mb-4">
              These limitations apply regardless of the legal theory (contract, tort, negligence, strict liability, etc.)
              and even if Wonderstood has been advised of the possibility of such damages.
            </p>

            <p className="mb-4">
              Some jurisdictions do not allow the exclusion or limitation of incidental or consequential damages, so
              the above limitations may not apply to you.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">16.2 Indemnification</h3>
            <p className="mb-4 font-semibold">
              You agree to indemnify, defend, and hold harmless Wonderstood, its officers, directors, employees, agents,
              affiliates, and licensors from and against any and all claims, liabilities, damages, losses, costs, expenses,
              or fees (including reasonable attorneys' fees) arising from or related to:
            </p>

            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Your breach of these Terms</li>
              <li>Your violation of any law, regulation, or third-party rights</li>
              <li>Your use or misuse of the Platform</li>
              <li>Content or information you provide (profiles, reviews, messages)</li>
              <li>Professional services you provide or receive through the Platform</li>
              <li>Disputes with other users</li>
              <li>Your negligence or willful misconduct</li>
              <li>Misrepresentation of your qualifications or credentials</li>
              <li>Violations of professional standards or licensing requirements</li>
            </ul>

            <p className="mb-4">
              We reserve the right to assume exclusive defense and control of any matter subject to indemnification,
              and you agree to cooperate with our defense. You will not settle any claim without our prior written consent.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">16.3 Mandatory Arbitration and Class Action Waiver</h3>

            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <p className="font-semibold text-red-900 mb-2">PLEASE READ THIS CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS:</p>
              <p className="text-red-900 font-semibold">
                BY AGREEING TO THESE TERMS, YOU AGREE TO RESOLVE DISPUTES THROUGH BINDING ARBITRATION AND WAIVE YOUR
                RIGHT TO A JURY TRIAL OR CLASS ACTION.
              </p>
            </div>

            <p className="mb-4"><strong>Informal Resolution (Required First Step):</strong></p>
            <p className="mb-4">
              Before initiating arbitration, you must first attempt to resolve disputes informally by contacting
              support@wonderstood.com with a detailed description of the dispute. We will attempt to resolve the
              issue within 30 days. If we cannot resolve the dispute, either party may proceed to arbitration.
            </p>

            <p className="mb-4"><strong>Binding Arbitration:</strong></p>
            <p className="mb-4">
              Any dispute, claim, or controversy arising out of or relating to these Terms or use of the Platform that
              cannot be resolved informally shall be settled by binding arbitration administered by the American
              Arbitration Association (AAA) under its Consumer Arbitration Rules.
            </p>

            <p className="mb-4">Arbitration Terms:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>The arbitration shall be conducted in [specify location/county and state] or remotely via video conference</li>
              <li>The arbitration shall be conducted in English</li>
              <li>The arbitrator's decision is final and binding</li>
              <li>Judgment on the arbitration award may be entered in any court with jurisdiction</li>
              <li>Each party bears their own costs and attorneys' fees unless the arbitrator awards fees to the prevailing party</li>
              <li>The arbitrator may award any relief that would be available in court, including injunctive relief</li>
              <li>Discovery will be permitted as necessary for a fair hearing</li>
            </ul>

            <p className="mb-4"><strong>Class Action Waiver:</strong></p>
            <p className="mb-4 font-semibold uppercase">
              ALL DISPUTES MUST BE BROUGHT INDIVIDUALLY. YOU AND WONDERSTOOD AGREE TO WAIVE ANY RIGHT TO HAVE ANY
              DISPUTE BE BROUGHT, HEARD, ADMINISTERED, OR RESOLVED AS A CLASS ACTION, COLLECTIVE ACTION, PRIVATE ATTORNEY
              GENERAL ACTION, OR IN ANY OTHER PROCEEDING WHERE EITHER PARTY ACTS IN A REPRESENTATIVE CAPACITY.
            </p>

            <p className="mb-4"><strong>Small Claims Court Exception:</strong></p>
            <p className="mb-4">
              Either party may bring a claim in small claims court instead of arbitration if the claim qualifies for
              small claims court jurisdiction and is brought in the proper venue.
            </p>

            <p className="mb-4"><strong>Opt-Out Right:</strong></p>
            <p className="mb-4">
              You may opt out of this arbitration agreement by sending written notice to support@wonderstood.com within
              30 days of first accepting these Terms. The notice must include your name, email address, and a clear
              statement that you wish to opt out of the arbitration agreement. If you opt out, you and Wonderstood
              agree to resolve disputes through the courts as described in Section 16.4.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">16.4 Governing Law and Venue</h3>
            <p className="mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of [Delaware/California/your state],
              United States, without regard to its conflict of law provisions.
            </p>
            <p className="mb-4">
              If arbitration does not apply (e.g., you opted out, or the claim is brought in small claims court), you
              agree that exclusive venue for any litigation shall be in the state and federal courts located in [specify county/city and state].
              You consent to personal jurisdiction in these courts and waive any objection to venue.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">16.5 Time Limitation on Claims</h3>
            <p className="mb-4">
              You agree that any claim or cause of action arising from or related to use of the Platform or these Terms
              must be filed within ONE (1) YEAR after the claim or cause of action arose. Otherwise, the claim is permanently barred.
            </p>
          </section>

          {/* Force Majeure */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Force Majeure</h2>
            <p className="mb-4">
              Wonderstood shall not be liable for any failure or delay in performance due to circumstances beyond our
              reasonable control, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Acts of God, natural disasters, earthquakes, floods, fires</li>
              <li>War, terrorism, civil unrest, riots</li>
              <li>Government actions, regulations, or orders</li>
              <li>Internet, telecommunications, or utility failures</li>
              <li>Third-party service provider outages (Stripe, Daily.co, Firebase/Google Cloud)</li>
              <li>Pandemics, epidemics, or public health emergencies</li>
              <li>Labor disputes, strikes, or lockouts</li>
              <li>Cyber attacks or data breaches affecting our infrastructure</li>
            </ul>
            <p className="mb-4">
              During a force majeure event, our obligations under these Terms will be suspended for the duration of
              the event. We will make reasonable efforts to minimize the impact and resume normal operations as soon as possible.
            </p>
          </section>

          {/* General Provisions */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. General Provisions</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.1 Entire Agreement</h3>
            <p className="mb-4">
              These Terms, together with our Privacy Policy and Cancellation Policy, constitute the entire agreement
              between you and Wonderstood regarding use of the Platform and supersede all prior agreements and understandings.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.2 Modifications to Terms</h3>
            <p className="mb-4">
              We may modify these Terms at any time. Material changes will be notified via email and/or prominent notice
              on the Platform at least 30 days before they take effect. Continued use of the Platform after changes
              take effect constitutes acceptance of the modified Terms.
            </p>
            <p className="mb-4">
              If you do not agree to the modified Terms, you must stop using the Platform and may delete your account.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.3 Severability</h3>
            <p className="mb-4">
              If any provision of these Terms is found to be unlawful, void, or unenforceable, that provision shall be
              deemed severable and shall not affect the validity and enforceability of the remaining provisions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.4 No Waiver</h3>
            <p className="mb-4">
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right
              or provision. Any waiver must be in writing and signed by an authorized representative of Wonderstood.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.5 Assignment</h3>
            <p className="mb-4">
              You may not assign or transfer these Terms or your account without our prior written consent. We may
              assign these Terms at any time without notice, including in connection with a merger, acquisition, or
              sale of assets.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.6 No Third-Party Beneficiaries</h3>
            <p className="mb-4">
              These Terms do not create any third-party beneficiary rights except as expressly stated.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.7 Headings</h3>
            <p className="mb-4">
              Section headings are for convenience only and do not affect the interpretation of these Terms.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.8 Electronic Communications</h3>
            <p className="mb-4">
              By using the Platform, you consent to receive electronic communications from us, including emails, notices
              posted on the Platform, and messages sent through the Platform. You agree that all agreements, notices,
              disclosures, and other communications that we provide electronically satisfy any legal requirement that
              such communications be in writing.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.9 Export Control</h3>
            <p className="mb-4">
              You may not use or export the Platform in violation of U.S. export control laws and regulations. You
              represent that you are not located in a country subject to U.S. embargo and are not on any U.S. government
              list of prohibited or restricted parties.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">18.10 Relationship of Parties</h3>
            <p className="mb-4">
              These Terms do not create any agency, partnership, joint venture, employment, or franchisee relationship
              between you and Wonderstood. You have no authority to bind Wonderstood or create any obligation on our behalf.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Contact Information</h2>
            <p className="mb-4">
              If you have questions about these Terms or need to contact us for any reason, please reach out:
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <p className="mb-2"><strong>Support:</strong> support@wonderstood.com</p>
              <p className="mb-2"><strong>Mailing Address:</strong></p>
              {/* <p className="ml-4">
                Wonderstood, Inc.<br />
                [Street Address]<br />
                [City, State ZIP]<br />
                United States
              </p> */}
            </div>

            <p className="mb-4">
              For notices of claimed copyright infringement or intellectual property concerns, please email support@wonderstood.com.
            </p>
          </section>

          {/* Acknowledgment */}
          <section className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
            <h2 className="text-2xl font-bold text-indigo-900 mb-4">Acknowledgment</h2>
            <p className="text-indigo-900 mb-4 font-semibold">
              BY USING THE WONDERSTOOD PLATFORM, YOU ACKNOWLEDGE THAT:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-indigo-900">
              <li>You have read and understood these Terms of Service</li>
              <li>You agree to be bound by these Terms</li>
              <li>You meet the eligibility requirements (18+ years old)</li>
              <li>You understand Wonderstood's role as a third-party marketplace</li>
              <li>You understand that professionals are independent contractors, not Wonderstood employees</li>
              <li>You accept the disclaimers of warranties and limitations of liability</li>
              <li>You agree to resolve disputes through binding arbitration</li>
              <li>You waive your right to bring or participate in class actions</li>
              <li>You accept all risks associated with use of the Platform</li>
            </ul>
            <p className="mt-4 text-indigo-900 italic">
              Last Updated: January 21, 2025
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
